import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
  type Dispatch,
  type ReactNode,
} from 'react'
import type {
  BorLetterRow,
  BorStateStatus,
  IllinoisLoginMode,
  LicenseRow,
  OnboardingContact,
  OtrTransactionRow,
  ProducerAppRow,
  ServiceType,
  StateLoginRow,
} from '../types/onboarding'
import {
  type FilingsCurrentStatus,
  type StepId,
  getStepsForService,
  hasPendingLoginWizardRows,
  isOnboardingContactComplete,
  nextVisibleStepIndex,
  prevVisibleStepIndex,
  shouldShowFloridaGeorgiaMississippiUserAccountAccess,
  shouldShowMaineTaxPortalAccess,
  shouldShowOregonNorthCarolinaUserAccountAccess,
  shouldShowRhodeIslandTaxPortalAccess,
  shouldShowIdahoAgreement,
  shouldShowNewMexicoTapAccess,
  shouldShowNewYorkAdminAccountAccess,
  shouldShowTexasAdminAccountAccess,
  shouldShowVermontMyVaxAccess,
  stepIndexForId,
} from '../types/onboarding'
import {
  loginRowCompleteForNewLoginWizard,
  loginRowCompleteForPrimaryStep,
} from '../wizard/licenseSteps/loginRowComplete'
import {
  INITIAL_ONBOARDING_CONTACTS,
  MOCK_BOR_STATES,
  MOCK_LICENSES,
  MOCK_OTR_ROWS,
  MOCK_PRODUCERS,
} from '../data/mockSeed'
import {
  reconcileStateLogins,
  createCustomStateLoginRow,
  illinoisLoginModeBlocksContinue,
  isCustomStateLoginRowId,
} from '../data/stateLoginRegistry'
import { shouldShowSeparateMiscAccountGuide } from '../data/stateLoginPortalMap'

type AgreementStatus = 'pending' | 'signed'

export interface OnboardingState {
  serviceType: ServiceType | null
  /** Service chosen on the entry step before Continue applies `SET_SERVICE`. */
  entryServiceSelection: ServiceType | null
  stepIndex: number
  agreementStatus: AgreementStatus
  licenses: LicenseRow[]
  contacts: OnboardingContact[]
  contactsEditing: boolean
  contactsConfirmed: boolean
  stateLogins: StateLoginRow[]
  borRows: BorLetterRow[]
  otrTransitionDate: string
  otrRows: OtrTransactionRow[]
  otrReportUploaded: boolean
  otrReconciliationAck: boolean
  weeklyAttested: boolean
  producers: ProducerAppRow[]
  branchUnderBranch: boolean | null
  trainingCompleted: boolean
  miscHandledStates: string[]
  transitionRequestDate: string
  filingsCurrent: FilingsCurrentStatus
  filingsExplanation: string
  attestationInitials: string
  /** State codes each DRLP individual is responsible for (subset of miscHandledStates). */
  drlpStateAssignments: Record<string, string[]>
  /** States where the client writes or plans to write business (subset of miscHandledStates). */
  activeBusinessStates: string[]
  /**
   * Illinois SL Logins branch: individual logins vs corporate account.
   * Resets to `unset` when IL leaves activeBusinessStates.
   */
  illinoisLoginMode: IllinoisLoginMode
  /** Set true after OPTins “separate MISC account” mock guide is completed (footer Continue). */
  separateMiscAccountGuideAcknowledged: boolean
  /** Set true after the Idaho third-party acknowledgement form is reviewed. */
  idahoAgreementAcknowledged: boolean
  /** Set true after Texas admin account creation instructions are acknowledged. */
  texasAdminAccountAccessAcknowledged: boolean
  /** Set true after OR/NC user account creation instructions are acknowledged. */
  oregonNorthCarolinaUserAccountAccessAcknowledged: boolean
  /** Set true after New York admin account creation instructions are acknowledged. */
  newYorkAdminAccountAccessAcknowledged: boolean
  /** Set true after FL/GA/MS user account creation instructions are acknowledged. */
  floridaGeorgiaMississippiUserAccountAccessAcknowledged: boolean
  /** Set true after Maine Tax Portal third-party access instructions are acknowledged. */
  maineTaxPortalAccessAcknowledged: boolean
  /** Set true after Rhode Island Tax Portal third-party access instructions are acknowledged. */
  rhodeIslandTaxPortalAccessAcknowledged: boolean
  /** Set true after Vermont MyVax third-party access instructions are acknowledged. */
  vermontMyVaxAccessAcknowledged: boolean
  /** Set true after New Mexico TAP third-party access instructions are acknowledged. */
  newMexicoTapAccessAcknowledged: boolean
  toast: string | null
  /** Bumps on RESET so step components remount with fresh local state. */
  demoNonce: number
}

type Action =
  | { type: 'SET_SERVICE'; serviceType: ServiceType }
  | { type: 'SET_ENTRY_SELECTION'; serviceType: ServiceType }
  | { type: 'NEXT' }
  | { type: 'PREV' }
  | { type: 'GO_STEP'; index: number }
  | { type: 'SIGN_AGREEMENT' }
  | { type: 'UPDATE_LICENSE'; id: string; patch: Partial<LicenseRow> }
  | { type: 'SET_DRLP'; licenseeId: string }
  | { type: 'ADD_LICENSE'; row: LicenseRow }
  | { type: 'BULK_UPDATE_LICENSES'; ids: string[]; patch: Partial<LicenseRow> }
  | { type: 'CONFIRM_CONTACTS' }
  | { type: 'UPDATE_CONTACT'; id: string; patch: Partial<OnboardingContact> }
  | { type: 'ADD_CONTACT'; contact: OnboardingContact }
  | { type: 'SET_CONTACTS_EDITING'; editing: boolean }
  | { type: 'UPDATE_LOGIN'; stateCode: string; patch: Partial<StateLoginRow> }
  | { type: 'ADD_STATE_LOGIN' }
  | { type: 'REMOVE_STATE_LOGIN'; stateCode: string }
  | { type: 'BOR_STATUS'; stateCode: string; status: BorStateStatus }
  | { type: 'SET_OTR_DATE'; date: string }
  | { type: 'ACK_RECONCILIATION' }
  | { type: 'UPLOAD_OTR_REPORT' }
  | { type: 'UPDATE_OTR_ROW'; id: string; patch: Partial<OtrTransactionRow> }
  | { type: 'ATTEST_WEEKLY' }
  | { type: 'UPDATE_PRODUCER'; npn: string; patch: Partial<ProducerAppRow> }
  | { type: 'SET_BRANCH'; value: boolean }
  | { type: 'TOGGLE_TRAINING' }
  | { type: 'SET_MISC_HANDLED_STATES'; codes: string[] }
  | { type: 'TOGGLE_MISC_HANDLED_STATE'; code: string }
  | { type: 'SET_ACTIVE_BUSINESS_STATES'; codes: string[] }
  | { type: 'TOGGLE_ACTIVE_BUSINESS_STATE'; code: string }
  | { type: 'SET_ILLINOIS_LOGIN_MODE'; mode: IllinoisLoginMode }
  | { type: 'SET_TRANSITION_REQUEST_DATE'; date: string }
  | { type: 'SET_FILINGS_CURRENT'; value: FilingsCurrentStatus }
  | { type: 'SET_FILINGS_EXPLANATION'; text: string }
  | { type: 'SET_ATTESTATION_INITIALS'; initials: string }
  | { type: 'SET_DRLP_STATES'; licenseeId: string; codes: string[] }
  | { type: 'TOGGLE_DRLP_STATE'; licenseeId: string; code: string }
  | { type: 'TOAST'; message: string }
  | { type: 'CLEAR_TOAST' }
  | { type: 'RESET' }
  | { type: 'ACK_SEPARATE_MISC_ACCOUNT_GUIDE' }
  | { type: 'ACK_IDAHO_AGREEMENT' }
  | { type: 'ACK_TEXAS_ADMIN_ACCOUNT_ACCESS' }
  | { type: 'ACK_OREGON_NORTH_CAROLINA_USER_ACCOUNT_ACCESS' }
  | { type: 'ACK_NEW_YORK_ADMIN_ACCOUNT_ACCESS' }
  | { type: 'ACK_FLORIDA_GEORGIA_MISSISSIPPI_USER_ACCOUNT_ACCESS' }
  | { type: 'ACK_MAINE_TAX_PORTAL_ACCESS' }
  | { type: 'ACK_RHODE_ISLAND_TAX_PORTAL_ACCESS' }
  | { type: 'ACK_VERMONT_MYVAX_ACCESS' }
  | { type: 'ACK_NEW_MEXICO_TAP_ACCESS' }

const initialState: OnboardingState = {
  serviceType: 'SurplusLines',
  entryServiceSelection: null,
  stepIndex: 0,
  agreementStatus: 'pending',
  licenses: MOCK_LICENSES.map((r) => ({ ...r })),
  contacts: INITIAL_ONBOARDING_CONTACTS.map((c) => ({ ...c })),
  contactsEditing: false,
  contactsConfirmed: false,
  stateLogins: reconcileStateLogins([], [], [], MOCK_LICENSES.map((r) => ({ ...r }))),
  borRows: MOCK_BOR_STATES.map((r) => ({ ...r })),
  otrTransitionDate: '',
  otrRows: MOCK_OTR_ROWS.map((r) => ({ ...r })),
  otrReportUploaded: false,
  otrReconciliationAck: false,
  weeklyAttested: false,
  producers: MOCK_PRODUCERS.map((p) => ({ ...p })),
  branchUnderBranch: null,
  trainingCompleted: false,
  miscHandledStates: [],
  transitionRequestDate: '',
  filingsCurrent: '',
  filingsExplanation: '',
  attestationInitials: '',
  drlpStateAssignments: {},
  activeBusinessStates: [],
  illinoisLoginMode: 'unset',
  separateMiscAccountGuideAcknowledged: false,
  idahoAgreementAcknowledged: false,
  texasAdminAccountAccessAcknowledged: false,
  oregonNorthCarolinaUserAccountAccessAcknowledged: false,
  newYorkAdminAccountAccessAcknowledged: false,
  floridaGeorgiaMississippiUserAccountAccessAcknowledged: false,
  maineTaxPortalAccessAcknowledged: false,
  rhodeIslandTaxPortalAccessAcknowledged: false,
  vermontMyVaxAccessAcknowledged: false,
  newMexicoTapAccessAcknowledged: false,
  toast: null,
  demoNonce: 0,
}

function drlpAssignmentsForLicenses(
  licenses: LicenseRow[],
  assignments: Record<string, string[]>,
): Record<string, string[]> {
  const drlpIds = new Set(
    licenses.filter((l) => l.entity === 'Individual' && l.isDrlp).map((l) => l.licenseeId),
  )
  return Object.fromEntries(
    Object.entries(assignments).filter(([id]) => drlpIds.has(id)),
  )
}

function clampStepIndex(stepIndex: number, serviceType: ServiceType | null): number {
  const steps = getStepsForService(serviceType)
  return Math.min(Math.max(0, stepIndex), Math.max(0, steps.length - 1))
}

function illinoisModeForActiveStates(
  activeBusinessStates: readonly string[],
  current: IllinoisLoginMode,
): IllinoisLoginMode {
  const writingIl = activeBusinessStates.some((c) => c.toUpperCase() === 'IL')
  return writingIl ? current : 'unset'
}

function reducer(state: OnboardingState, action: Action): OnboardingState {
  switch (action.type) {
    case 'SET_SERVICE':
      return {
        ...state,
        serviceType: action.serviceType,
        entryServiceSelection: null,
      }
    case 'SET_ENTRY_SELECTION':
      return { ...state, entryServiceSelection: action.serviceType }
    case 'NEXT': {
      const steps = getStepsForService(state.serviceType)
      const next = Math.min(state.stepIndex + 1, steps.length - 1)
      return { ...state, stepIndex: next }
    }
    case 'PREV': {
      const next = Math.max(state.stepIndex - 1, 0)
      return { ...state, stepIndex: next }
    }
    case 'GO_STEP': {
      const nextIdx = clampStepIndex(action.index, state.serviceType)
      const full = getStepsForService(state.serviceType)
      const targetId = full[nextIdx] ?? 'welcome'
      return {
        ...state,
        stepIndex: nextIdx,
        separateMiscAccountGuideAcknowledged:
          targetId === 'logins' ? false : state.separateMiscAccountGuideAcknowledged,
        idahoAgreementAcknowledged:
          targetId === 'logins' ? false : state.idahoAgreementAcknowledged,
        texasAdminAccountAccessAcknowledged:
          targetId === 'logins' ? false : state.texasAdminAccountAccessAcknowledged,
        oregonNorthCarolinaUserAccountAccessAcknowledged:
          targetId === 'logins'
            ? false
            : state.oregonNorthCarolinaUserAccountAccessAcknowledged,
        newYorkAdminAccountAccessAcknowledged:
          targetId === 'logins' ? false : state.newYorkAdminAccountAccessAcknowledged,
        floridaGeorgiaMississippiUserAccountAccessAcknowledged:
          targetId === 'logins'
            ? false
            : state.floridaGeorgiaMississippiUserAccountAccessAcknowledged,
        maineTaxPortalAccessAcknowledged:
          targetId === 'logins' ? false : state.maineTaxPortalAccessAcknowledged,
        rhodeIslandTaxPortalAccessAcknowledged:
          targetId === 'logins' ? false : state.rhodeIslandTaxPortalAccessAcknowledged,
        vermontMyVaxAccessAcknowledged:
          targetId === 'logins' ? false : state.vermontMyVaxAccessAcknowledged,
        newMexicoTapAccessAcknowledged:
          targetId === 'logins' ? false : state.newMexicoTapAccessAcknowledged,
      }
    }
    case 'SIGN_AGREEMENT':
      return { ...state, agreementStatus: 'signed', toast: 'Agreement marked signed (demo).' }
    case 'SET_DRLP': {
      const target = state.licenses.find((l) => l.licenseeId === action.licenseeId)
      if (!target) return state
      const st = target.licenseState
      if (target.isDrlp) {
        const licenses = state.licenses.map((r) =>
          r.licenseeId === action.licenseeId ? { ...r, isDrlp: false } : r,
        )
        const restAssignments = Object.fromEntries(
          Object.entries(state.drlpStateAssignments).filter(([id]) => id !== action.licenseeId),
        )
        return {
          ...state,
          licenses,
          drlpStateAssignments: drlpAssignmentsForLicenses(licenses, restAssignments),
        }
      }
      const licenses = state.licenses.map((r) =>
        r.licenseState === st
          ? { ...r, isDrlp: r.licenseeId === action.licenseeId }
          : r,
      )
      const synced = drlpAssignmentsForLicenses(licenses, state.drlpStateAssignments)
      return {
        ...state,
        licenses,
        drlpStateAssignments: {
          ...synced,
          [action.licenseeId]: synced[action.licenseeId] ?? [],
        },
      }
    }
    case 'UPDATE_LICENSE': {
      const licenses = state.licenses.map((r) =>
        r.licenseeId === action.id ? { ...r, ...action.patch } : r,
      )
      return {
        ...state,
        licenses,
        drlpStateAssignments: drlpAssignmentsForLicenses(licenses, state.drlpStateAssignments),
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          state.activeBusinessStates,
          state.stateLogins,
          licenses,
          state.illinoisLoginMode,
        ),
      }
    }
    case 'BULK_UPDATE_LICENSES': {
      const licenses = state.licenses.map((r) =>
        action.ids.includes(r.licenseeId) ? { ...r, ...action.patch } : r,
      )
      return {
        ...state,
        licenses,
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          state.activeBusinessStates,
          state.stateLogins,
          licenses,
          state.illinoisLoginMode,
        ),
      }
    }
    case 'ADD_LICENSE': {
      const licenses = [...state.licenses, action.row]
      const drlpStateAssignments =
        action.row.entity === 'Individual' && action.row.isDrlp
          ? { ...state.drlpStateAssignments, [action.row.licenseeId]: [] }
          : state.drlpStateAssignments
      return {
        ...state,
        licenses,
        drlpStateAssignments,
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          state.activeBusinessStates,
          state.stateLogins,
          licenses,
          state.illinoisLoginMode,
        ),
      }
    }
    case 'CONFIRM_CONTACTS':
      return {
        ...state,
        contactsConfirmed: true,
        contactsEditing: false,
        toast: 'Contacts confirmed.',
      }
    case 'UPDATE_CONTACT':
      return {
        ...state,
        contactsConfirmed: false,
        contacts: state.contacts.map((c) =>
          c.id === action.id ? { ...c, ...action.patch } : c,
        ),
      }
    case 'ADD_CONTACT':
      return {
        ...state,
        contactsConfirmed: false,
        contacts: [...state.contacts, action.contact],
      }
    case 'SET_CONTACTS_EDITING':
      return {
        ...state,
        contactsEditing: action.editing,
        contactsConfirmed: action.editing ? false : state.contactsConfirmed,
      }
    case 'UPDATE_LOGIN':
      // pendingLoginWizard is cleared only from SL Logins (checkbox), not when credentials are filled,
      // so deferred rows stay listed on New Login Wizard until the user unchecks defer on SL Logins.
      return {
        ...state,
        stateLogins: state.stateLogins.map((r) => {
          if (r.stateCode !== action.stateCode) return r
          const next = { ...r, ...action.patch }
          const complete = loginRowCompleteForPrimaryStep(next, state.licenses)
          return { ...next, complete }
        }),
      }
    case 'ADD_STATE_LOGIN': {
      const row = createCustomStateLoginRow(state.licenses, state.stateLogins)
      return {
        ...state,
        stateLogins: [...state.stateLogins, row],
      }
    }
    case 'REMOVE_STATE_LOGIN': {
      if (!isCustomStateLoginRowId(action.stateCode)) return state
      return {
        ...state,
        stateLogins: state.stateLogins.filter((r) => r.stateCode !== action.stateCode),
      }
    }
    case 'BOR_STATUS':
      return {
        ...state,
        borRows: state.borRows.map((b) =>
          b.stateCode === action.stateCode ? { ...b, status: action.status } : b,
        ),
      }
    case 'SET_OTR_DATE':
      return { ...state, otrTransitionDate: action.date }
    case 'ACK_RECONCILIATION':
      return { ...state, otrReconciliationAck: !state.otrReconciliationAck }
    case 'UPLOAD_OTR_REPORT':
      return {
        ...state,
        otrReportUploaded: true,
      }
    case 'UPDATE_OTR_ROW':
      return {
        ...state,
        otrRows: state.otrRows.map((r) =>
          r.id === action.id ? { ...r, ...action.patch } : r,
        ),
      }
    case 'ATTEST_WEEKLY':
      return { ...state, weeklyAttested: true, toast: 'Weekly attestation recorded (demo).' }
    case 'UPDATE_PRODUCER':
      return {
        ...state,
        producers: state.producers.map((p) =>
          p.npn === action.npn ? { ...p, ...action.patch } : p,
        ),
      }
    case 'SET_BRANCH':
      return { ...state, branchUnderBranch: action.value }
    case 'TOGGLE_TRAINING':
      return { ...state, trainingCompleted: !state.trainingCompleted }
    case 'SET_MISC_HANDLED_STATES': {
      const illinoisLoginMode = illinoisModeForActiveStates(action.codes, state.illinoisLoginMode)
      return {
        ...state,
        miscHandledStates: action.codes,
        activeBusinessStates: [...action.codes],
        illinoisLoginMode,
        idahoAgreementAcknowledged: false,
        texasAdminAccountAccessAcknowledged: false,
        oregonNorthCarolinaUserAccountAccessAcknowledged: false,
        newYorkAdminAccountAccessAcknowledged: false,
        floridaGeorgiaMississippiUserAccountAccessAcknowledged: false,
        maineTaxPortalAccessAcknowledged: false,
        rhodeIslandTaxPortalAccessAcknowledged: false,
        vermontMyVaxAccessAcknowledged: false,
        newMexicoTapAccessAcknowledged: false,
        stateLogins: reconcileStateLogins(
          action.codes,
          action.codes,
          state.stateLogins,
          state.licenses,
          illinoisLoginMode,
        ),
        drlpStateAssignments: Object.fromEntries(
          Object.entries(state.drlpStateAssignments).map(([id, codes]) => [
            id,
            codes.filter((c) => action.codes.includes(c)),
          ]),
        ),
      }
    }
    case 'TOGGLE_MISC_HANDLED_STATE': {
      const has = state.miscHandledStates.includes(action.code)
      const miscHandledStates = has
        ? state.miscHandledStates.filter((c) => c !== action.code)
        : [...state.miscHandledStates, action.code]
      const activeBusinessStates = has
        ? state.activeBusinessStates.filter((c) => c !== action.code)
        : state.activeBusinessStates.includes(action.code)
          ? state.activeBusinessStates
          : [...state.activeBusinessStates, action.code]
      const illinoisLoginMode = illinoisModeForActiveStates(
        activeBusinessStates,
        state.illinoisLoginMode,
      )
      return {
        ...state,
        miscHandledStates,
        activeBusinessStates,
        illinoisLoginMode,
        idahoAgreementAcknowledged: false,
        texasAdminAccountAccessAcknowledged: false,
        oregonNorthCarolinaUserAccountAccessAcknowledged: false,
        newYorkAdminAccountAccessAcknowledged: false,
        floridaGeorgiaMississippiUserAccountAccessAcknowledged: false,
        maineTaxPortalAccessAcknowledged: false,
        rhodeIslandTaxPortalAccessAcknowledged: false,
        vermontMyVaxAccessAcknowledged: false,
        newMexicoTapAccessAcknowledged: false,
        stateLogins: reconcileStateLogins(
          miscHandledStates,
          activeBusinessStates,
          state.stateLogins,
          state.licenses,
          illinoisLoginMode,
        ),
        drlpStateAssignments: Object.fromEntries(
          Object.entries(state.drlpStateAssignments).map(([id, codes]) => [
            id,
            codes.filter((c) => miscHandledStates.includes(c)),
          ]),
        ),
      }
    }
    case 'SET_ACTIVE_BUSINESS_STATES': {
      const activeBusinessStates = action.codes.filter((c) => state.miscHandledStates.includes(c))
      const illinoisLoginMode = illinoisModeForActiveStates(
        activeBusinessStates,
        state.illinoisLoginMode,
      )
      return {
        ...state,
        activeBusinessStates,
        illinoisLoginMode,
        idahoAgreementAcknowledged: false,
        texasAdminAccountAccessAcknowledged: false,
        oregonNorthCarolinaUserAccountAccessAcknowledged: false,
        newYorkAdminAccountAccessAcknowledged: false,
        floridaGeorgiaMississippiUserAccountAccessAcknowledged: false,
        maineTaxPortalAccessAcknowledged: false,
        rhodeIslandTaxPortalAccessAcknowledged: false,
        vermontMyVaxAccessAcknowledged: false,
        newMexicoTapAccessAcknowledged: false,
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          activeBusinessStates,
          state.stateLogins,
          state.licenses,
          illinoisLoginMode,
        ),
      }
    }
    case 'TOGGLE_ACTIVE_BUSINESS_STATE': {
      if (!state.miscHandledStates.includes(action.code)) return state
      const has = state.activeBusinessStates.includes(action.code)
      const activeBusinessStates = has
        ? state.activeBusinessStates.filter((c) => c !== action.code)
        : [...state.activeBusinessStates, action.code]
      const illinoisLoginMode = illinoisModeForActiveStates(
        activeBusinessStates,
        state.illinoisLoginMode,
      )
      return {
        ...state,
        activeBusinessStates,
        illinoisLoginMode,
        idahoAgreementAcknowledged: false,
        texasAdminAccountAccessAcknowledged: false,
        oregonNorthCarolinaUserAccountAccessAcknowledged: false,
        newYorkAdminAccountAccessAcknowledged: false,
        floridaGeorgiaMississippiUserAccountAccessAcknowledged: false,
        maineTaxPortalAccessAcknowledged: false,
        rhodeIslandTaxPortalAccessAcknowledged: false,
        vermontMyVaxAccessAcknowledged: false,
        newMexicoTapAccessAcknowledged: false,
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          activeBusinessStates,
          state.stateLogins,
          state.licenses,
          illinoisLoginMode,
        ),
      }
    }
    case 'SET_ILLINOIS_LOGIN_MODE': {
      const illinoisLoginMode = illinoisModeForActiveStates(
        state.activeBusinessStates,
        action.mode,
      )
      return {
        ...state,
        illinoisLoginMode,
        stateLogins: reconcileStateLogins(
          state.miscHandledStates,
          state.activeBusinessStates,
          state.stateLogins,
          state.licenses,
          illinoisLoginMode,
        ),
      }
    }
    case 'SET_TRANSITION_REQUEST_DATE':
      return { ...state, transitionRequestDate: action.date }
    case 'SET_FILINGS_CURRENT':
      return {
        ...state,
        filingsCurrent: action.value,
        filingsExplanation: action.value === 'no' ? state.filingsExplanation : '',
      }
    case 'SET_FILINGS_EXPLANATION':
      return {
        ...state,
        filingsExplanation: action.text.slice(0, 1000),
      }
    case 'SET_ATTESTATION_INITIALS':
      return { ...state, attestationInitials: action.initials }
    case 'SET_DRLP_STATES':
      return {
        ...state,
        drlpStateAssignments: {
          ...state.drlpStateAssignments,
          [action.licenseeId]: action.codes.filter((c) => state.miscHandledStates.includes(c)),
        },
      }
    case 'TOGGLE_DRLP_STATE': {
      const allowed = state.miscHandledStates
      if (!allowed.includes(action.code)) return state
      const current = state.drlpStateAssignments[action.licenseeId] ?? []
      const next = current.includes(action.code)
        ? current.filter((c) => c !== action.code)
        : [...current, action.code]
      return {
        ...state,
        drlpStateAssignments: {
          ...state.drlpStateAssignments,
          [action.licenseeId]: next,
        },
      }
    }
    case 'TOAST':
      return { ...state, toast: action.message }
    case 'CLEAR_TOAST':
      return { ...state, toast: null }
    case 'ACK_SEPARATE_MISC_ACCOUNT_GUIDE':
      return { ...state, separateMiscAccountGuideAcknowledged: true }
    case 'ACK_IDAHO_AGREEMENT':
      return { ...state, idahoAgreementAcknowledged: true }
    case 'ACK_TEXAS_ADMIN_ACCOUNT_ACCESS':
      return { ...state, texasAdminAccountAccessAcknowledged: true }
    case 'ACK_OREGON_NORTH_CAROLINA_USER_ACCOUNT_ACCESS':
      return { ...state, oregonNorthCarolinaUserAccountAccessAcknowledged: true }
    case 'ACK_NEW_YORK_ADMIN_ACCOUNT_ACCESS':
      return { ...state, newYorkAdminAccountAccessAcknowledged: true }
    case 'ACK_FLORIDA_GEORGIA_MISSISSIPPI_USER_ACCOUNT_ACCESS':
      return { ...state, floridaGeorgiaMississippiUserAccountAccessAcknowledged: true }
    case 'ACK_MAINE_TAX_PORTAL_ACCESS':
      return { ...state, maineTaxPortalAccessAcknowledged: true }
    case 'ACK_RHODE_ISLAND_TAX_PORTAL_ACCESS':
      return { ...state, rhodeIslandTaxPortalAccessAcknowledged: true }
    case 'ACK_VERMONT_MYVAX_ACCESS':
      return { ...state, vermontMyVaxAccessAcknowledged: true }
    case 'ACK_NEW_MEXICO_TAP_ACCESS':
      return { ...state, newMexicoTapAccessAcknowledged: true }
    case 'RESET':
      return {
        ...initialState,
        licenses: MOCK_LICENSES.map((r) => ({ ...r })),
        contacts: INITIAL_ONBOARDING_CONTACTS.map((c) => ({ ...c })),
        contactsEditing: false,
        stateLogins: reconcileStateLogins([], [], [], MOCK_LICENSES.map((r) => ({ ...r }))),
        borRows: MOCK_BOR_STATES.map((r) => ({ ...r })),
        otrRows: MOCK_OTR_ROWS.map((r) => ({ ...r })),
        producers: MOCK_PRODUCERS.map((p) => ({ ...p })),
        activeBusinessStates: [],
        demoNonce: state.demoNonce + 1,
      }
    default:
      return state
  }
}

interface Ctx {
  state: OnboardingState
  dispatch: Dispatch<Action>
  steps: ReturnType<typeof getStepsForService>
  currentStepId: ReturnType<typeof getStepsForService>[number]
  stepIndex: number
  canAdvance: boolean
  advance: () => void
  back: () => void
  stepBackOverride: (() => boolean) | null
  setStepBackOverride: (handler: (() => boolean) | null) => void
  goToStep: (stepId: StepId) => void
}

const OnboardingContext = createContext<Ctx | null>(null)

export function OnboardingProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(reducer, initialState)
  const [stepBackOverride, setStepBackOverrideState] = useState<(() => boolean) | null>(null)
  const setStepBackOverride = useCallback((handler: (() => boolean) | null) => {
    setStepBackOverrideState(() => handler)
  }, [])

  const steps = useMemo(() => getStepsForService(state.serviceType), [state.serviceType])
  const stepIndex = clampStepIndex(state.stepIndex, state.serviceType)
  const currentStepId = steps[stepIndex] ?? 'welcome'

  useEffect(() => {
    const full = getStepsForService(state.serviceType)
    const idx = clampStepIndex(state.stepIndex, state.serviceType)
    const currentId = full[idx]
    if (currentId === 'newLoginWizard' && !hasPendingLoginWizardRows(state.stateLogins)) {
      const skipTo = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (skipTo !== null) dispatch({ type: 'GO_STEP', index: skipTo })
      return
    }
    if (
      currentId === 'separateMiscAccountGuide' &&
      !shouldShowSeparateMiscAccountGuide(state.stateLogins, state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (currentId === 'idahoAgreement' && !shouldShowIdahoAgreement(state.activeBusinessStates)) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'texasAdminAccountAccess' &&
      !shouldShowTexasAdminAccountAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'oregonNorthCarolinaUserAccountAccess' &&
      !shouldShowOregonNorthCarolinaUserAccountAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'newYorkAdminAccountAccess' &&
      !shouldShowNewYorkAdminAccountAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'floridaGeorgiaMississippiUserAccountAccess' &&
      !shouldShowFloridaGeorgiaMississippiUserAccountAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'maineTaxPortalAccess' &&
      !shouldShowMaineTaxPortalAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'rhodeIslandTaxPortalAccess' &&
      !shouldShowRhodeIslandTaxPortalAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'vermontMyVaxAccess' &&
      !shouldShowVermontMyVaxAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
    if (
      currentId === 'newMexicoTapAccess' &&
      !shouldShowNewMexicoTapAccess(state.activeBusinessStates)
    ) {
      const nextIdx = nextVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (nextIdx !== null) {
        dispatch({ type: 'GO_STEP', index: nextIdx })
        return
      }
      const prevIdx = prevVisibleStepIndex(
        state.serviceType,
        state.stateLogins,
        state.activeBusinessStates,
        idx,
      )
      if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
    }
  }, [state.stepIndex, state.serviceType, state.stateLogins, state.activeBusinessStates])

  const canAdvance = useMemo(() => {
    switch (currentStepId) {
      case 'welcome':
      case 'checklist':
      case 'important':
        return true
      case 'generalQuestions':
        return (
          state.miscHandledStates.length > 0 &&
          state.activeBusinessStates.length > 0 &&
          state.transitionRequestDate.length > 0 &&
          (state.filingsCurrent === 'yes' ||
            (state.filingsCurrent === 'no' && state.filingsExplanation.trim().length > 0))
        )
      case 'attestation':
        return state.attestationInitials.trim().length > 0
      case 'businessLicenses':
      case 'individualLicenses':
        return true
      case 'drlpAssignment': {
        const drlps = state.licenses.filter(
          (l) => l.entity === 'Individual' && l.isDrlp,
        )
        if (drlps.length === 0) return true
        return drlps.every(
          (p) => (state.drlpStateAssignments[p.licenseeId]?.length ?? 0) > 0,
        )
      }
      case 'contacts':
        return (
          state.contactsConfirmed &&
          state.contacts.length > 0 &&
          state.contacts.every((c) => isOnboardingContactComplete(c))
        )
      case 'logins':
        if (illinoisLoginModeBlocksContinue(state.activeBusinessStates, state.illinoisLoginMode)) {
          return false
        }
        return state.stateLogins.every((s) => loginRowCompleteForPrimaryStep(s, state.licenses))
      case 'separateMiscAccountGuide':
        return state.separateMiscAccountGuideAcknowledged
      case 'newLoginWizard': {
        const pending = state.stateLogins.filter((r) => r.pendingLoginWizard)
        return pending.every((r) => loginRowCompleteForNewLoginWizard(r, state.licenses))
      }
      case 'idahoAgreement':
        return state.idahoAgreementAcknowledged
      case 'texasAdminAccountAccess':
        return state.texasAdminAccountAccessAcknowledged
      case 'oregonNorthCarolinaUserAccountAccess':
        return state.oregonNorthCarolinaUserAccountAccessAcknowledged
      case 'newYorkAdminAccountAccess':
        return state.newYorkAdminAccountAccessAcknowledged
      case 'floridaGeorgiaMississippiUserAccountAccess':
        return state.floridaGeorgiaMississippiUserAccountAccessAcknowledged
      case 'maineTaxPortalAccess':
        return state.maineTaxPortalAccessAcknowledged
      case 'rhodeIslandTaxPortalAccess':
        return state.rhodeIslandTaxPortalAccessAcknowledged
      case 'vermontMyVaxAccess':
        return state.vermontMyVaxAccessAcknowledged
      case 'newMexicoTapAccess':
        return state.newMexicoTapAccessAcknowledged
      case 'bor':
        return true
      case 'otr':
        return (
          state.otrTransitionDate.length > 0 &&
          state.otrReconciliationAck &&
          state.otrReportUploaded
        )
      case 'applications':
        return (
          state.branchUnderBranch !== null &&
          state.producers.every((p) => p.appStatus === 'signed' || p.appStatus === 'sent')
        )
      case 'training':
        return state.trainingCompleted
      case 'complete':
        return false
      default:
        return true
    }
  }, [currentStepId, state])

  const advance = useCallback(() => {
    const nextIdx = nextVisibleStepIndex(
      state.serviceType,
      state.stateLogins,
      state.activeBusinessStates,
      state.stepIndex,
    )
    if (nextIdx !== null) dispatch({ type: 'GO_STEP', index: nextIdx })
  }, [dispatch, state.serviceType, state.stateLogins, state.activeBusinessStates, state.stepIndex])

  const back = useCallback(() => {
    const prevIdx = prevVisibleStepIndex(
      state.serviceType,
      state.stateLogins,
      state.activeBusinessStates,
      state.stepIndex,
    )
    if (prevIdx !== null) dispatch({ type: 'GO_STEP', index: prevIdx })
  }, [dispatch, state.serviceType, state.stateLogins, state.activeBusinessStates, state.stepIndex])

  const goToStep = useCallback(
    (stepId: StepId) => {
      if (stepId === 'newLoginWizard' && !hasPendingLoginWizardRows(state.stateLogins)) {
        return
      }
      if (
        stepId === 'separateMiscAccountGuide' &&
        !shouldShowSeparateMiscAccountGuide(state.stateLogins, state.activeBusinessStates)
      ) {
        return
      }
      if (stepId === 'idahoAgreement' && !shouldShowIdahoAgreement(state.activeBusinessStates)) {
        return
      }
      if (
        stepId === 'texasAdminAccountAccess' &&
        !shouldShowTexasAdminAccountAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'oregonNorthCarolinaUserAccountAccess' &&
        !shouldShowOregonNorthCarolinaUserAccountAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'newYorkAdminAccountAccess' &&
        !shouldShowNewYorkAdminAccountAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'floridaGeorgiaMississippiUserAccountAccess' &&
        !shouldShowFloridaGeorgiaMississippiUserAccountAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'maineTaxPortalAccess' &&
        !shouldShowMaineTaxPortalAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'rhodeIslandTaxPortalAccess' &&
        !shouldShowRhodeIslandTaxPortalAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'vermontMyVaxAccess' &&
        !shouldShowVermontMyVaxAccess(state.activeBusinessStates)
      ) {
        return
      }
      if (
        stepId === 'newMexicoTapAccess' &&
        !shouldShowNewMexicoTapAccess(state.activeBusinessStates)
      ) {
        return
      }
      dispatch({ type: 'GO_STEP', index: stepIndexForId(steps, stepId) })
    },
    [dispatch, steps, state.stateLogins, state.activeBusinessStates],
  )

  const value = useMemo(
    () => ({
      state,
      dispatch,
      steps,
      currentStepId,
      stepIndex,
      canAdvance,
      advance,
      back,
      stepBackOverride,
      setStepBackOverride,
      goToStep,
    }),
    [
      state,
      dispatch,
      steps,
      currentStepId,
      stepIndex,
      canAdvance,
      advance,
      back,
      stepBackOverride,
      setStepBackOverride,
      goToStep,
    ],
  )

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>
}

/** Hook for wizard steps; co-located with provider (fast-refresh expects single export per file). */
export function useOnboarding() {
  const ctx = useContext(OnboardingContext)
  if (!ctx) throw new Error('useOnboarding outside provider')
  return ctx
}
